import cn from 'classnames';
import Link from 'next/link';
// Types
import {
  FeaturedStreamsType,
  getFeaturedStreamActionState,
} from '../FeaturedStreamsCarousel.helpers';
// UI
import Text from 'ui3/Text/Text';
// Components
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Hooks
import { useGetCurrUser, useStreamPurchaseNavigation } from 'hooks';
// Helpers
import { computeOrderStreamPath } from 'helpers/routes';
import { formatStreamDate } from 'helpers/dateText';
import { parseForInnerHTML } from 'helpers/textEditor';
// Styles
import styles from './MobileCarousel.module.scss';

interface MobileCarouselProps {
  streams: FeaturedStreamsType[];
  currentSlide: number;
  onSlideChange: (slideIndex: number) => void;
  useArrowNavigation?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  showOfficialPpvTag?: boolean;
}

const MobileCarousel = ({
  streams,
  currentSlide,
  onSlideChange,
  useArrowNavigation = false,
  onPrev,
  onNext,
  showOfficialPpvTag = true,
}: MobileCarouselProps) => {
  const { data: userData } = useGetCurrUser();
  const { navigateToStream } = useStreamPurchaseNavigation();
  const currentStream = streams?.[currentSlide];

  if (!currentStream || !currentStream.stream) {
    return null;
  }

  const stream = currentStream.stream;
  const {
    buttonText,
    hasAccessToWatch,
    showOfficialPpvTagForSlide,
  } = getFeaturedStreamActionState(
    stream,
    userData?.me?.id,
    showOfficialPpvTag
  );

  const handleBuyButtonClick = () => {
    navigateToStream(currentStream.stream, hasAccessToWatch);
  };

  // Parse description for ellipsis
  const descriptionPlainText = currentStream.stream.description
    ? parseForInnerHTML(currentStream.stream.description).replace(
        /<[^>]*>/g,
        ''
      )
    : '';

  const showIndicators = streams.length > 1;

  return (
    <div className={styles.mobileCarousel}>
      {/* Image Section with Tags Overlay */}
      <div className={styles.imageSection}>
        <div className={styles.imageWrapper}>
          {currentStream.stream.imageURL && (
            <img
              src={currentStream.stream.imageURL}
              alt={currentStream.stream.name || 'Event poster'}
              className={styles.posterImage}
            />
          )}
          <div className={styles.imageShading} />
          <div className={styles.mobileImageOverlap}>
            <div className={styles.tagsRow}>
              {showOfficialPpvTagForSlide && (
                <div className={styles.officialPpvTag}>
                  <Text variant="smallLabelBold" color="background-dim">
                    Official PPV
                  </Text>
                </div>
              )}
              <div className={styles.dateTag}>
                <Text variant="smallLabelBold" color="background-dim">
                  {formatStreamDate(currentStream.stream.scheduleDateUtc)}
                </Text>
              </div>
            </div>
            <Link
              href={computeOrderStreamPath(currentStream.stream)}
              prefetch={false}
            >
              <a className={styles.eventTitleLink}>
                <h2 className={styles.eventTitle}>
                  {currentStream.stream.name}
                </h2>
              </a>
            </Link>
          </div>
        </div>

        {/* Arrow Navigation on Sides */}
        {useArrowNavigation && streams.length > 1 && (
          <>
            <button
              type="button"
              className={styles.arrowButtonLeft}
              onClick={onPrev}
              aria-label="Previous"
            >
              <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
            </button>
            <button
              type="button"
              className={styles.arrowButtonRight}
              onClick={onNext}
              aria-label="Next"
            >
              <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
            </button>
          </>
        )}
      </div>

      {/* Content Section Below Image */}
      <div className={styles.contentSection}>
        {descriptionPlainText && (
          <div className={styles.description}>{descriptionPlainText}</div>
        )}

        <Button
          variant="primary"
          size="large"
          className={styles.buyButton}
          onClick={handleBuyButtonClick}
        >
          {buttonText}
        </Button>

        {/* Carousel Indicators */}
        {showIndicators && (
          <div className={styles.indicators}>
            {streams.map((_, index) => (
              <button
                type="button"
                key={index}
                className={cn(styles.indicator, {
                  [styles.active]: index === currentSlide,
                })}
                onClick={() => onSlideChange(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCarousel;
